// apps/web/src/components/teams/TeamManager.tsx
// Team management component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Team } from '../../types/team';
import { apiClient } from '../../lib/apiClient';

export function TeamManager() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/teams');
        setTeams(response.data.teams);
      } catch (err) {
        setError('Failed to load teams');
        console.error('Error loading teams:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, []);

  const handleCreateTeam = async (teamData: any) => {
    try {
      const response = await apiClient.post('/api/v1/teams', teamData);
      setTeams([response.data, ...teams]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create team');
      console.error('Error creating team:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading teams...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="team-manager">
      <div className="header">
        <h2>Team Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Team'}
        </button>
      </div>

      {showCreateForm && (
        <TeamForm 
          onSubmit={handleCreateTeam} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="teams-grid">
        {teams.map(team => (
          <TeamCard key={team.id} team={team} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function TeamCard({ team }: { team: Team }) {
  return (
    <div className="team-card">
      <div className="team-header">
        <h3>{team.name}</h3>
        <span className="team-status">{team.status}</span>
      </div>
      <p>{team.description || 'No description'}</p>
      <p><strong>Workspace:</strong> {team.workspaceId.substring(0, 8)}...</p>
      <p><strong>Members:</strong> {team.memberCount || 0}</p>
      <p><strong>Created:</strong> {new Date(team.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Edit</button>
        <button className="btn btn-danger">Delete</button>
      </div>
    </div>
  );
}

// Helper component
function TeamForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    workspaceId: '',
    members: [] as string[],
    status: 'ACTIVE'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="team-form">
      <h3>Create New Team</h3>
      <input
        type="text"
        placeholder="Team Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <input
        type="text"
        placeholder="Workspace ID"
        value={formData.workspaceId}
        onChange={(e) => setFormData({...formData, workspaceId: e.target.value})}
        required
      />
      <select
        value={formData.status}
        onChange={(e) => setFormData({...formData, status: e.target.value})}
      >
        <option value="ACTIVE">Active</option>
        <option value="INACTIVE">Inactive</option>
        <option value="ARCHIVED">Archived</option>
      </select>
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Team</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}